<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Analise de Negocios',
  },
});

const activeTab = ref('distribution');

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

const distribution = computed(() => props.data?.value_distribution || {});
const topDeals = computed(() => props.data?.top_deals || []);
const recentDeals = computed(() => props.data?.recent_deals || []);
const dealsByStatus = computed(() => props.data?.by_status || {});

function getStatusColor(status) {
  const colors = {
    deal_open: 'bg-n-blue-9',
    deal_won: 'bg-n-teal-9',
    deal_lost: 'bg-n-ruby-9',
  };
  return colors[status] || 'bg-n-slate-9';
}

function getStatusLabel(status) {
  const labels = {
    deal_open: 'Em Aberto',
    deal_won: 'Ganho',
    deal_lost: 'Perdido',
  };
  return labels[status] || status;
}
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-trending-up text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex gap-1">
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeTab === 'distribution'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'distribution'"
        >
          Distribuicao
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeTab === 'top'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'top'"
        >
          Top Negocios
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeTab === 'recent'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'recent'"
        >
          Recentes
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Distribution View -->
    <div v-else-if="activeTab === 'distribution'" class="flex flex-col gap-4">
      <!-- Value Ranges -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Ate R$ 1.000</div>
          <div class="text-xl font-bold text-n-slate-12">
            {{ formatNumber(distribution.small) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">R$ 1K - R$ 10K</div>
          <div class="text-xl font-bold text-n-blue-11">
            {{ formatNumber(distribution.medium) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">R$ 10K - R$ 100K</div>
          <div class="text-xl font-bold text-n-violet-11">
            {{ formatNumber(distribution.large) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Acima R$ 100K</div>
          <div class="text-xl font-bold text-n-teal-11">
            {{ formatNumber(distribution.enterprise) }}
          </div>
        </div>
      </div>

      <!-- By Status -->
      <div class="p-3 rounded-lg bg-n-alpha-1">
        <div class="text-xs font-medium text-n-slate-11 mb-3">Por Status</div>
        <div class="flex items-center gap-4">
          <div
            v-for="(value, status) in dealsByStatus"
            :key="status"
            class="flex items-center gap-2"
          >
            <span class="w-3 h-3 rounded" :class="getStatusColor(status)" />
            <span class="text-sm text-n-slate-12">{{ getStatusLabel(status) }}: {{ formatNumber(value) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Deals View -->
    <div v-else-if="activeTab === 'top'" class="flex flex-col gap-2">
      <div
        v-if="topDeals.length === 0"
        class="text-center text-n-slate-11 py-8"
      >
        Nenhum negocio encontrado
      </div>
      <div
        v-for="(deal, idx) in topDeals"
        :key="deal.id"
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <span
            class="w-6 h-6 flex items-center justify-center rounded-full bg-n-blue-3 text-n-blue-11 text-xs font-bold"
          >
            {{ idx + 1 }}
          </span>
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">{{
              deal.contact_name || 'Sem contato'
            }}</span>
            <span class="text-xs text-n-slate-11">{{ deal.funnel_name }} - {{ deal.stage_name }}</span>
          </div>
        </div>
        <div class="flex flex-col items-end">
          <span class="text-lg font-bold text-n-teal-11">{{
            formatCurrency(deal.value)
          }}</span>
          <span
            class="text-xs"
            :class="getStatusColor(deal.deal_status).replace('bg-', 'text-')"
          >
            {{ getStatusLabel(deal.deal_status) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Recent Deals View -->
    <div v-else-if="activeTab === 'recent'" class="flex flex-col gap-2">
      <div
        v-if="recentDeals.length === 0"
        class="text-center text-n-slate-11 py-8"
      >
        Nenhum negocio recente
      </div>
      <div
        v-for="deal in recentDeals"
        :key="deal.id"
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <span
            class="w-2 h-2 rounded-full"
            :class="getStatusColor(deal.deal_status)"
          />
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">{{
              deal.contact_name || 'Sem contato'
            }}</span>
            <span class="text-xs text-n-slate-11">{{ deal.stage_name }}</span>
          </div>
        </div>
        <div class="flex flex-col items-end">
          <span class="text-sm font-bold text-n-teal-11">{{
            formatCurrency(deal.value)
          }}</span>
          <span class="text-xs text-n-slate-10">{{
            formatDate(deal.created_at)
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
